import axios from "axios";
import { BACKEND_URL } from "../environment/environment";
import { useAuthStore } from "../stores/auth.store";
import { refreshAccessToken } from "./mutations/auth.mutations";

export const publicAxios = axios.create({
	baseURL: BACKEND_URL,
	headers: {
		"Content-Type": "application/json",
	},
});

export const privateAxios = axios.create({
	baseURL: BACKEND_URL,
	headers: {
		"Content-Type": "application/json",
	},
});

let interceptorId: number | null = null;
let isRefreshing = false;
let failedQueue: Array<{
	resolve: (value: string) => void;
	reject: (reason: Error) => void;
}> = [];

const processQueue = (error: Error | null, token: string | null = null) => {
	for (const prom of failedQueue) {
		if (error) {
			prom.reject(error);
		} else if (token) {
			prom.resolve(token);
		}
	}

	failedQueue = [];
};

export const attachToken = (token: string) => {
	if (interceptorId !== null) {
		privateAxios.interceptors.request.eject(interceptorId);
	}

	interceptorId = privateAxios.interceptors.request.use(
		(config) => {
			config.headers.Authorization = `Bearer ${token}`;
			return config;
		},
		(error) => {
			return Promise.reject(error);
		},
	);
};

export const initializeAuth = () => {
	const state = useAuthStore.getState();
	const { accessToken, impersonationToken } = state;

	if (accessToken) {
		attachToken(accessToken);
	}

	if (impersonationToken) {
		attachToken(impersonationToken);
	}

	privateAxios.interceptors.response.use(
		(res) => res,
		async (err) => {
			const originalRequest = err.config;

			if (err.response?.status === 401 && !originalRequest._retry) {
				const state = useAuthStore.getState();
				const { refreshToken, email, impersonationToken } = state;

				if (impersonationToken) {
					state.setImpersonationToken(null);
				}

				if (refreshToken && email && !isRefreshing) {
					isRefreshing = true;
					originalRequest._retry = true;

					try {
						const response = await refreshAccessToken({ refreshToken, email });

						if (response.AuthenticationResult?.AccessToken) {
							const newAccessToken = response.AuthenticationResult.AccessToken;
							const newRefreshToken =
								response.AuthenticationResult.RefreshToken || refreshToken;

							state.login(newAccessToken, newRefreshToken, email);
							attachToken(newAccessToken);

							processQueue(null, newAccessToken);
							isRefreshing = false;

							originalRequest.headers.Authorization = `Bearer ${newAccessToken}`;
							return privateAxios(originalRequest);
						}

						const error = new Error(
							"Token refresh failed: No access token returned",
						);
						processQueue(error, null);
						state.logout();
						isRefreshing = false;
						return Promise.reject(error);
					} catch (refreshError) {
						processQueue(refreshError as Error, null);
						state.logout();
						isRefreshing = false;
						return Promise.reject(refreshError);
					}
				} else if (isRefreshing) {
					return new Promise((resolve, reject) => {
						failedQueue.push({ resolve, reject });
					}).then((token) => {
						originalRequest.headers.Authorization = `Bearer ${token}`;
						return privateAxios(originalRequest);
					});
				} else {
					state.logout();
					return Promise.reject(err);
				}
			}

			return Promise.reject(err);
		},
	);
};
